// The in-bundle error reporter — ADR-0032, DEV_TECH_DESIGN-0032 §5.2.
//
// The comfortable path: a proper dialog for a user who still has a working
// page. Its counterpart, templates/partials/_error_reporter.html, handles the
// case where this file never ran at all. The two share no code by design (D-1)
// and meet only at two window globals:
//
//   window.__shepwiseErrorStage  — written by the inline script, read here
//   window.__shepwiseReady       — written by main.js, read by the inline script
//
// Everything here is entered by a user click or by an error that has already
// happened, so a healthy page pays nothing for it (NFR-3): the modal is not
// built until the first report.

import { bootstrap, axios } from './vendor.js';

const ENDPOINT = '/api/error-report';
const MODAL_ID = 'shepwiseErrorReportModal';
const SUBMIT_ID = 'shepwiseErrorSubmit';

// One report per click, no concurrent sends, no automatic retry (FR-8).
let inFlight = false;

// Set by chat.js. A callback rather than an import is what lets this module
// stay ignorant of chat internals — it must not import chat.js.
let contextProvider = null;

// The most recent failed request, staged by chat.js before it renders the
// error bubble that will offer to report it.
let stagedRequest = null;

// What the currently open dialog is reporting. Module state rather than an
// argument threaded through the click handler, because the click happens long
// after openReportDialog returned.
let currentSource = 'unknown';

let modal = null;
let noteField = null;
let statusLine = null;
let submitButton = null;
let initialised = false;

export function setContextProvider(fn) {
    contextProvider = fn;
}

export function stageRequestFailure(error, endpoint) {
    stagedRequest = {
        message: error?.message || '',
        error_type: error?.name || 'Error',
        http_status: error?.response?.status || null,
        endpoint: endpoint || '',
    };
}

function buildPayload(note) {
    const staged = window.__shepwiseErrorStage || {};
    const request = stagedRequest || {};
    const context = (contextProvider && contextProvider()) || {};
    return {
        error_source: currentSource,
        message: request.message || staged.message || '',
        error_type: request.error_type || staged.error_type || '',
        source_url: staged.source_url || '',
        line: staged.line || null,
        column: staged.column || null,
        stack: staged.stack || '',
        http_status: request.http_status || null,
        endpoint: request.endpoint || '',
        page_path: window.location.pathname,
        persona_id: context.persona_id || '',
        model_id: context.model_id || '',
        user_agent: navigator.userAgent,
        client_timestamp: new Date().toISOString(),
        note: note || '',
    };
}

// Created in JavaScript rather than added to a template: a page that never
// errors carries no markup for it, and no base template needs a second edit.
function buildModal() {
    const root = document.createElement('div');
    root.className = 'modal fade';
    root.id = MODAL_ID;
    root.tabIndex = -1;
    root.setAttribute('aria-hidden', 'true');
    root.innerHTML = `
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Fehler melden</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Schließen"></button>
            </div>
            <div class="modal-body">
              <label for="shepwiseErrorNote" class="form-label">Was ist passiert? (optional)</label>
              <textarea id="shepwiseErrorNote" class="form-control" rows="3"></textarea>
              <p class="form-text mb-0">
                Technische Angaben zur Seite werden mitgesendet — keine Frage- oder Antworttexte.
              </p>
              <p class="mt-3 mb-0" id="shepwiseErrorStatus" role="status"></p>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Abbrechen</button>
              <button type="button" class="btn btn-primary" id="${SUBMIT_ID}">Senden</button>
            </div>
          </div>
        </div>`;
    document.body.appendChild(root);

    noteField = root.querySelector('#shepwiseErrorNote');
    statusLine = root.querySelector('#shepwiseErrorStatus');
    submitButton = root.querySelector(`#${SUBMIT_ID}`);
    modal = new bootstrap.Modal(root);
}

async function submitReport() {
    if (inFlight) return;
    inFlight = true;
    submitButton.disabled = true;
    statusLine.textContent = 'Wird gesendet …';
    try {
        await axios.post(ENDPOINT, buildPayload(noteField.value));
        statusLine.textContent = 'Danke — der Fehler wurde gemeldet.';
    } catch (error) {
        // Deliberately swallowed, and this is the point of the whole module:
        // a failed report must cost the user nothing. No thrown error, no
        // second dialog, nothing that could re-enter the capture handlers
        // (NFR-3, US-6), and no retry. Both outcomes are one line of text.
        statusLine.textContent = 'Das Melden hat nicht funktioniert. Bitte später erneut versuchen.';
    }
    // The button stays disabled: this dialog sends once.
}

export function openReportDialog(source) {
    if (!modal) buildModal();
    // Each opening is a fresh report; the previous one's state must not leak.
    currentSource = source;
    inFlight = false;
    noteField.value = '';
    statusLine.textContent = '';
    submitButton.disabled = false;
    modal.show();
}

export function initErrorReporter() {
    if (initialised) return;
    initialised = true;
    // One delegated listener on the document, not on the send button: the
    // modal does not exist yet on a page that never errors, and delegating
    // here is what keeps its construction lazy without a "first time?" branch
    // inside openReportDialog.
    document.addEventListener('click', event => {
        if (event.target.closest(`#${SUBMIT_ID}`)) submitReport();
    });
}
